import { doc, collection, setDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';

// Generate a unique ID for new properties
export const generatePropertyId = () => {
    return 'prop_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
};

// Convert string to array
export const stringToArray = (str) => {
    return str ? str.split(',').map(item => item.trim()).filter(item => item) : [];
};

// Prepare temp array fields when starting to edit
export const prepareArrayFields = (propertyData) => {
    const preparedFields = {};
    
    ['propertyHighlights', 'mainFeatures', 'amenities', 'additionalImagesUrl', 
     'houseRules', 'cancellationPolicy', 'paymentPolicy'].forEach(field => {
        preparedFields[field] = Array.isArray(propertyData[field]) 
            ? [...propertyData[field]] 
            : stringToArray(propertyData[field] || '');
    });
    
    return preparedFields;
};

// Add new property to Firestore
export const addProperty = async (newProperty, tempArrayFields, fetchProperties) => {
    // Validate form
    if (!newProperty.propertyTitle || !newProperty.location || !newProperty.startPrice) {
        toast.error("Please fill in at least Property Title, Location and Start Price");
        return { success: false };
    }

    try {
        toast.info("Adding new property...", { autoClose: false, toastId: 'adding-property' });
        const propertyId = generatePropertyId();
        
        // Parse values with better handling of numeric fields
        const propertyData = {
            propertyTitle: newProperty.propertyTitle.trim(),
            propertyType: newProperty.propertyType ? newProperty.propertyType.trim() : '', // Add propertyType
            customPropertyType: newProperty.customPropertyType ? newProperty.customPropertyType.trim() : '', // Add customPropertyType
            location: newProperty.location.trim(),
            description: newProperty.description ? newProperty.description.trim() : '',
            // For numeric fields, ensure they're properly parsed or default to 0
            startPrice: newProperty.startPrice === '' ? 0 : Math.max(0, parseFloat(newProperty.startPrice) || 0),
            bhkCount: newProperty.bhkCount === '' ? 0 : Math.max(0, parseInt(newProperty.bhkCount) || 0),
            maxOccupancy: newProperty.maxOccupancy === '' ? 0 : Math.max(0, parseInt(newProperty.maxOccupancy) || 0),
            beds: newProperty.beds === '' ? 0 : Math.max(0, parseInt(newProperty.beds) || 0),
            baths: newProperty.baths === '' ? 0 : Math.max(0, parseInt(newProperty.baths) || 0),
            tag: newProperty.tag ? newProperty.tag.trim() : '',
            mapLink: newProperty.mapLink ? newProperty.mapLink.trim() : '',
            propertyHighlights: tempArrayFields.propertyHighlights.filter(item => item.trim()),
            mainFeatures: tempArrayFields.mainFeatures.filter(item => item.trim()),
            amenities: tempArrayFields.amenities.filter(item => item.trim()),
            coverImageUrl: newProperty.coverImageUrl ? newProperty.coverImageUrl.trim() : '',
            additionalImagesUrl: tempArrayFields.additionalImagesUrl.filter(item => item.trim()),
            brochureLink: newProperty.brochureLink ? newProperty.brochureLink.trim() : '',
            mealsBrochure: newProperty.mealsBrochure ? newProperty.mealsBrochure.trim() : '',
            houseRules: tempArrayFields.houseRules.filter(item => item.trim()),
            cancellationPolicy: tempArrayFields.cancellationPolicy.filter(item => item.trim()),
            paymentPolicy: tempArrayFields.paymentPolicy.filter(item => item.trim()),
            createdAt: serverTimestamp(),
            viewCount: 0,  // Initialize view count
            lastViewed: null
        };
        
        // Update the allproperties document with the new property
        const propertiesDocRef = doc(collection(db, 'properties'), 'allproperties');
        await setDoc(propertiesDocRef, { [propertyId]: propertyData }, { merge: true });
        
        if (fetchProperties) {
            fetchProperties();
        }
        toast.dismiss('adding-property');
        toast.success("Property added successfully!");
        return { success: true };
    } catch (error) {
        console.error("Error adding property: ", error);
        toast.dismiss('adding-property');
        toast.error(`Failed to add property: ${error.message || "Unknown error"}`);
        return { success: false, error };
    }
};

// Save edited property to Firestore
export const updateProperty = async (editingPropertyId, propertyData, tempArrayFields, fetchProperties) => {
    // Validate
    if (!propertyData.propertyTitle || !propertyData.location || !propertyData.startPrice) {
        toast.error("Please fill in at least Property Title, Location and Start Price");
        return { success: false };
    }

    try {
        toast.info("Updating property...", { autoClose: false, toastId: 'updating-property' });
        
        // Process array fields with better handling of numeric values
        const processedPropertyData = {
            ...propertyData,
            propertyType: propertyData.propertyType ? propertyData.propertyType.trim() : '', // Add propertyType
            customPropertyType: propertyData.customPropertyType ? propertyData.customPropertyType.trim() : '', // Add customPropertyType
            description: propertyData.description ? propertyData.description.trim() : '',
            // For numeric fields, handle empty strings and ensure non-negative values
            startPrice: propertyData.startPrice === '' ? 0 : Math.max(0, parseFloat(propertyData.startPrice) || 0),
            bhkCount: propertyData.bhkCount === '' ? 0 : Math.max(0, parseInt(propertyData.bhkCount) || 0),
            maxOccupancy: propertyData.maxOccupancy === '' ? 0 : Math.max(0, parseInt(propertyData.maxOccupancy) || 0),
            beds: propertyData.beds === '' ? 0 : Math.max(0, parseInt(propertyData.beds) || 0),
            baths: propertyData.baths === '' ? 0 : Math.max(0, parseInt(propertyData.baths) || 0),
            tag: propertyData.tag ? propertyData.tag.trim() : '',
            mapLink: propertyData.mapLink ? propertyData.mapLink.trim() : '',
            propertyHighlights: tempArrayFields.propertyHighlights.filter(item => item.trim()),
            mainFeatures: tempArrayFields.mainFeatures.filter(item => item.trim()),
            amenities: tempArrayFields.amenities.filter(item => item.trim()),
            additionalImagesUrl: tempArrayFields.additionalImagesUrl.filter(item => item.trim()),
            houseRules: tempArrayFields.houseRules.filter(item => item.trim()),
            cancellationPolicy: tempArrayFields.cancellationPolicy.filter(item => item.trim()),
            paymentPolicy: tempArrayFields.paymentPolicy.filter(item => item.trim()),
            updatedAt: serverTimestamp()
        };
        
        // Update the property in the allproperties document
        const propertiesDocRef = doc(collection(db, 'properties'), 'allproperties');
        await setDoc(propertiesDocRef, { [editingPropertyId]: processedPropertyData }, { merge: true });
        
        if (fetchProperties) {
            fetchProperties();
        }
        toast.dismiss('updating-property');
        toast.success("Property updated successfully!");
        return { success: true };
    } catch (error) {
        console.error("Error updating property: ", error);
        toast.dismiss('updating-property');
        toast.error(`Failed to update property: ${error.message || "Unknown error"}`);
        return { success: false, error };
    }
};

// Delete property from Firestore
export const deleteProperty = async (propertyId, fetchProperties) => {
    try {
        toast.info("Deleting property...", { autoClose: false, toastId: 'deleting-property' });
        // Remove the property from the allproperties document
        const propertiesDocRef = doc(collection(db, 'properties'), 'allproperties');
        await setDoc(propertiesDocRef, { [propertyId]: deleteField() }, { merge: true });
        
        if (fetchProperties) {
            fetchProperties();
        }
        toast.dismiss('deleting-property');
        toast.success("Property deleted successfully!");
        return { success: true };
    } catch (error) {
        console.error("Error deleting property: ", error);
        toast.dismiss('deleting-property');
        toast.error(`Failed to delete property: ${error.message || "Unknown error"}`);
        return { success: false, error };
    }
};
